import { useState, useCallback, useRef, useEffect } from 'react'

import { message } from 'antd'
import { useTranslation } from 'react-i18next'

/**
 * 商品搜尋 Hook
 *
 * @param {Object} productService - Product Service (admin 或 operator)
 * @param {Object} options - 配置選項
 * @param {number} options.minSearchLength - 最少搜尋字元數 (預設: 2)
 * @param {number} options.limit - 返回商品數量上限 (預設: 50)
 * @param {number} options.debounceDelay - Debounce 延遲時間 (ms) (預設: 300)
 * @param {string} options.merchantId - 篩選指定 merchant 的商品
 *
 * @returns {Object} - { products, loading, searchProducts, clearProducts }
 */
export default function useProductSearch(productService, options = {}) {
  const { t } = useTranslation()
  const {
    minSearchLength = 3,
    limit = 50,
    debounceDelay = 300,
    merchantId,
    excludeIds = []
  } = options

  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [inputValue, setInputValue] = useState('')
  const debounceTimer = useRef(null)

  // 清理 debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  // 當 merchantId 或 excludeIds 變更時，清空已載入的商品列表
  useEffect(() => {
    setProducts([])
  }, [merchantId, JSON.stringify(excludeIds)])

  // 實際執行搜尋的函數
  const performSearch = useCallback(async (searchText) => {
    // 清空條件
    if (!searchText || searchText.length < minSearchLength) {
      setProducts([])
      setSearchKeyword('')
      return
    }

    setLoading(true)
    setSearchKeyword(searchText)
    try {
      const params = {
        search: searchText,
        limit: limit
      }
      if (merchantId) {
        params.merchant_id = merchantId
      }
      const data = await productService.fetchList(params)

      if (data.success) {
        let productOptions = (data.data || []).map(product => ({
          value: product.id,
          label: `${product.sku} - ${product.name}`,
          sku: product.sku,
          name: product.name,
          price: product.price,
          // 保留所有原始資料以備不時之需
          ...product
        }))

        // 排除指定的 IDs
        if (excludeIds.length > 0) {
          productOptions = productOptions.filter(p => !excludeIds.includes(p.value))
        }

        setProducts(productOptions)
      } else {
        setProducts([])
      }
    } catch (error) {
      console.error('[useProductSearch] Search failed:', error)
      message.error(t('common.error.fetch_failed'))
      setProducts([])
    } finally {
      setLoading(false)
    }
  }, [productService, minSearchLength, limit, merchantId, excludeIds, t])

  // 帶 debounce 的搜尋函數
  const searchProducts = useCallback((searchText) => {
    setInputValue(searchText || '')

    // 清除舊的 timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    // 如果搜尋文字不足，立即清空
    if (!searchText || searchText.length < minSearchLength) {
      setProducts([])
      setSearchKeyword('')
      setLoading(false)
      return
    }

    // 設定新的 debounce
    setLoading(true)
    debounceTimer.current = setTimeout(() => {
      performSearch(searchText)
    }, debounceDelay)
  }, [performSearch, debounceDelay, minSearchLength])

  const clearProducts = useCallback(() => {
    setProducts([])
    setSearchKeyword('')
    setInputValue('')
  }, [])

  return {
    products,       // 商品選項陣列
    loading,        // 載入狀態
    searchKeyword,  // 當前搜尋關鍵字
    inputValue,     // 當前輸入文字
    minSearchLength, // 最少搜尋字數
    searchProducts, // 搜尋函數
    clearProducts   // 清空商品列表
  }
}
